import React, { useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { FileInput, Input, InputGroup } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import PageTitle from '@components/PageTitle'
import Template from '@components/Template'
import { successMessages } from '@constants/feedbackMessages'
import { yupResolver } from '@hookform/resolvers/yup'
import { legalDocumentSchema } from '@schemas/legalDocument.schema'
import { legalDocumentApi } from '@services/LegalDocumentApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { createBreadcrumbLink, useCreateBreadcrumb } from '@utils/hooks'

type Inputs = {
	file: FileList
	title: string
	nicknames?: string
	scopes?: string
	sourceUrl?: string
}

// Separa "a, b, c" em lista, ignorando o que ficou em branco.
const toList = (value?: string): string[] =>
	(value ?? '')
		.split(',')
		.map((item) => item.trim())
		.filter((item) => item.length > 0)

function NewLegalDocument() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const baseUrl = '/acervo'

	const allPages = useMemo(
		() => [
			createBreadcrumbLink('Acervo de documentos', baseUrl, 1),
			createBreadcrumbLink('Subir documento', `${baseUrl}/novo`, 2),
		],
		[]
	)

	const pages = useCreateBreadcrumb(allPages)

	const [createDocument, { isSuccess, isLoading, data, error }] =
		legalDocumentApi.useCreateMutation()

	const { register, handleSubmit, control } = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(legalDocumentSchema),
	})

	/*
	 * A carga é síncrona, então o relatório já vem na resposta. Só se ele
	 * nomear a norma há para onde navegar: súmulas e temas entram às dezenas de
	 * uma vez e não têm endereço único.
	 */
	useEffect(() => {
		if (isSuccess && data) {
			dispatch(notificationCreators.show(successMessages.sentLegalDocument))

			if (data.slug) {
				navigate(`${baseUrl}/${data.slug}`)
			} else {
				navigate(baseUrl)
			}
		}
	}, [isSuccess, data, dispatch, navigate])

	const onSubmit: SubmitHandler<Inputs> = (form) => {
		createDocument({
			file: form.file,
			title: form.title || undefined,
			nicknames: toList(form.nicknames),
			scopes: toList(form.scopes),
			sourceUrl: form.sourceUrl || undefined,
		})
	}

	const failure = (error as { data?: { message?: string; detail?: string } })
		?.data

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title='Subir documento para o acervo' />

			<p className='mt-2 mb-2 text-gray max-w-[820px] leading-relaxed'>
				Envie a norma inteira, em PDF, HTML ou texto. Ela é separada em
				dispositivos, cada trecho conferido contra o próprio documento, e passa
				a substituir no acervo os artigos que a busca na internet havia trazido
				soltos. Não há tipo a escolher: como o documento se divide é detectado
				do próprio texto.
			</p>

			<form onSubmit={handleSubmit(onSubmit)} className='mt-6'>
				<div className='grid grid-cols-2 gap-4'>
					<Controller
						control={control}
						name='file'
						render={({ field, fieldState }) => (
							<FileInput
								accept='application/pdf,text/html,text/plain,.htm,.html,.txt'
								onChange={(files) => field.onChange(files)}
								isValid={fieldState.isTouched ? !fieldState.error : undefined}
							>
								Arquivo da norma
							</FileInput>
						)}
					/>
				</div>

				<div className='grid grid-cols-2 gap-4 mt-6'>
					<InputGroup
						label='Citação oficial, que define o endereço (opcional)'
						id='title'
						hint='A citação formal da norma, com número e ano: "Lei nº 13.105/2015" vira o endereço lei-13105-2015. Em branco, sai da citação impressa na abertura do documento. Preencha quando a norma não tiver número: código de corregedoria, constituição estadual. Dá para corrigir depois.'
					>
						<Input
							type='text'
							placeholder='Lei nº 13.105/2015'
							{...register('title')}
						/>
					</InputGroup>

					<InputGroup
						label='Como a prova cita (opcional)'
						id='nicknames'
						hint='Os outros nomes e siglas por onde a prova cita esta norma, separados por vírgula. Cadastrar aqui é o que faz a busca chegar direto na norma. Vale só para norma: súmula e tema entram uma por enunciado.'
					>
						<Input
							type='text'
							placeholder='Código Civil, CC'
							{...register('nicknames')}
						/>
					</InputGroup>

					<InputGroup
						label='A quem se aplica (opcional)'
						id='scopes'
						hint='Separe por vírgula. Em branco, sai do nome da norma ou da abertura do documento; não achando, ela vale como nacional e concorre em toda prova. Preencha quando for norma de um lugar e o documento não disser isso.'
					>
						<Input
							type='text'
							placeholder='espírito santo'
							{...register('scopes')}
						/>
					</InputGroup>

					<InputGroup
						label='Endereço oficial'
						id='sourceUrl'
						hint='A página de onde a norma veio. Opcional, e é o que permite recarregá-la quando a lei for alterada. Sem ele, atualizar exige achar o documento de novo.'
					>
						<Input
							type='text'
							placeholder='https://www.planalto.gov.br/ccivil_03/leis/l9882.htm'
							{...register('sourceUrl')}
						/>
					</InputGroup>
				</div>

				{failure && (
					<div className='mt-4'>
						<p className='text-red'>{failure.message}</p>
						{failure.detail && (
							<p className='mt-1 text-sm text-red'>{failure.detail}</p>
						)}
					</div>
				)}

				<div className='flex gap-4 mt-6 mb-10'>
					<Button type='submit' disabled={isLoading}>
						{isLoading ? 'Processando o documento...' : 'Subir documento'}
					</Button>
					<Button
						type='button'
						variant='outline'
						onClick={() => navigate(baseUrl)}
					>
						Cancelar
					</Button>
				</div>

				{isLoading && (
					<p className='mb-10 text-sm text-gray'>
						O documento é lido e separado em dispositivos nesta mesma chamada.
						Uma norma grande leva alguns minutos, então não feche a página.
					</p>
				)}
			</form>
		</Template>
	)
}

export default NewLegalDocument
